<script lang="ts" setup>
import { UserContactDetail } from "@/graphql/operations";
import { PropType, computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  contactDetails: {
    type: Object as PropType<UserContactDetail>,
    required: true,
  },
});

const { t } = useI18n();

const normalizeAppointmentBookingUrl = (rawUrl?: string | null) => {
  const value = rawUrl?.trim();
  if (!value) {
    return null;
  }
  if (value.startsWith("//")) {
    return `https:${value}`;
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    return value;
  }
  return `https://${value}`;
};

const appointmentBookingUrl = computed(() =>
  normalizeAppointmentBookingUrl(props.contactDetails.appointmentBookingUrl),
);
</script>

<template>
  <div class="contact_details">
    <div class="image_container">
      <img
        :src="contactDetails.profileImageUrl"
        v-if="contactDetails.profileImageUrl"
      />
      <div class="img_placeholder" v-else>
        <span class="material-symbols-outlined">person</span>
      </div>
    </div>

    <div class="info">
      <div>{{ contactDetails.displayName }}</div>
      <div class="subinfo">{{ contactDetails.jobTitle }}</div>
      <div class="info_row">
        <span class="material-symbols-outlined" v-if="contactDetails.phone"
          >call</span
        >
        {{ contactDetails.phone }}
      </div>
      <div class="info_row" v-if="appointmentBookingUrl">
        <span class="material-symbols-outlined">event</span>
        <a
          class="value"
          :href="appointmentBookingUrl"
          target="_blank"
          rel="noopener noreferrer"
          >{{ t("documentSpace.contactDetails.appointmentBookingCta") }}</a
        >
      </div>
      <div class="info_row">
        <span class="material-symbols-outlined">mail</span>
        <span class="value">
          {{ contactDetails.email }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$border-strong: #d0d4d9;
.contact_details {
  display: flex;
  flex: 1;
  line-height: 1.6em;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info {
  color: inherit;
}

.image_container {
  height: 96px;
  width: 96px;
  border-radius: 50%;
  overflow: hidden;
}

.image_container .img_placeholder,
.image_container img {
  display: inline-block;
  height: auto;
  width: 100%;
}

.image_container .img_placeholder {
  align-items: center;
  justify-content: center;
  display: flex;
}

.image_container .img_placeholder span {
  font-size: 60px;
  color: var(--classic-border-strong, $border-strong);
}

.info {
  flex: 1;
  padding: 0 1.5em;
}

.subinfo {
  margin-bottom: 1.5em;
}

.material-symbols-outlined {
  font-size: 1em;
  vertical-align: middle;
  margin-right: 0.3em;
}

.info_row {
  display: flex;
  align-items: center;
}

.value {
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
